import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Trophy,
  Calendar,
  MapPin,
  Users,
  Video,
  DollarSign,
  ListOrdered,
  Star,
  ExternalLink,
} from "lucide-react";
import juniorsHeroBasketballCourt from "@/assets/juniors-hero-basketball-court.jpg";
import juniorsCircuitTrophies from "@/assets/rl_hoops_juniors_circuit_trophies.jpeg";
import aboutCircuitBackground from "@/assets/about-circuit-background.jpg";
import juniorsLeagueFormat from "@/assets/juniors_league_format.png";
import juniorsAgeGroups from "@/assets/juniors_age_groups.png";
import juniorsLeagueFees from "@/assets/juniors_league_fees.png";
import sectionImage4 from "@/assets/section-image-4.jpg";
import { UpcomingEvents } from "@/components/hoops/UpcomingEvents";
import { Badge as InfoBadge } from "@/components/ui/badge";

// Juniors category ID from database
const JUNIORS_CATEGORY_ID = "d56f838b-3864-4950-98b6-606adcfadc8b";

export default function HoopsJuniorsCircuit() {
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={juniorsCircuitTrophies}
            alt="RecruitLook Hoops Juniors Circuit Championship Trophies"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-secondary text-black px-6 py-2 font-black mb-6">JUNIORS</Badge>
           <h1 className="text-3xl sm:text-4xl lg:text-7xl font-black mb-2 leading-tight">
             RECRUITLOOK
             <span className="block text-secondary">HOOPS</span>
           </h1>
           <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black">JUNIORS CIRCUIT</h2>
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="pt-8 pb-8 md:pt-12 md:pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 flex-wrap">
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/boys-circuit">BOYS CIRCUIT</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/girls-circuit">GIRLS CIRCUIT</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/larry-hughes-elite-camp">ELITE CAMP</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/juniors-circuit">JUNIORS CIRCUIT</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <InfoBadge className="bg-secondary text-black px-4 py-2 font-black mb-4">UPCOMING EVENTS</InfoBadge>
            <h2 className="text-3xl lg:text-5xl font-black mb-4">Juniors Circuit Events</h2>
            <p className="text-xl text-gray-600">Join us at exclusive 11u-14u tournaments and showcases</p>
          </div>
          <UpcomingEvents sportSlug="basketball" sportName="basketball" categoryId={JUNIORS_CATEGORY_ID} />
        </div>
      </section>

      {/* Circuit Perks */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">CIRCUIT PERKS</Badge>
            <h2 className="text-3xl lg:text-5xl font-black mb-2">Calling All 11u–14u Teams</h2>
            <p className="text-gray-700 text-lg">Win our circuit and get rewarded!</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PerkCard title="14u Winner" items={["$500 Cash", "Automatic Invite to RL Hoops HS Circuit next year"]} />
            <PerkCard title="13u Winner" items={["$500 Cash", "Free entry next season to the Finale"]} />
            <PerkCard title="12u Winner" items={["$500 Cash", "Free entry next season to the Finale"]} />
            <PerkCard title="11u Winner" items={["$500 Cash", "Free entry next season to the Finale"]} />
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src={aboutCircuitBackground} 
            alt="Basketball player with ball"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">ABOUT THE CIRCUIT</Badge>
          <h3 className="text-3xl font-black mb-4">RecruitLook Juniors Circuit</h3>
          <div className="space-y-4 text-gray-200 leading-relaxed max-w-4xl">
            <p>We're excited to announce the 2026 RecruitLook Hoops Junior Circuit NEXT UP that will feature premiere clubs at the 5th, 6th, 7th and 8th grade levels. Teams from across the country will compete in a series of high-level events, followed by a bracket finale championship to determine who is NEXT UP.</p>
            <p>The conclusion of the Circuit happens in St. Louis. Following the Circuit Finale, we'll host an Elite Youth Camp where the top hoopers from the Circuit get invited. Campers will receive top-level instruction from the area's best high school coaches and former professional players. Additionally, RecruitLook Hoops media will provide recruiting coverage and media distribution.</p>
          </div>
        </div>
      </section>

      {/* Details rows */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {/* League Format */}
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <div className="bg-secondary text-slate-900 rounded-2xl p-6 flex flex-col justify-center">
              <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-slate-900"><ListOrdered className="w-5 h-5 text-slate-900" /> League Format</h4>
              <ul className="list-disc pl-6 space-y-2 text-slate-900">
                <li>Teams must play at least 2 sessions to qualify for the cash prize during the Bracket Finale. Teams can compete in as many events as they choose. The more you play, the better your standings improve.</li>
                <li>If a team plays in 4 of the events, then their prize payouts double if they win the Finale.</li>
                <li>All events are open to any team. You can play in the Open Division and not be in the circuit — we will have games for any and all teams.</li>
                <li>The Finale will be held in May in St. Louis. It will be a March Madness-style bracket based on the standings from the events. Winner take all!</li>
                <li>All of our events are also where the High School Circuit is. High School and Middle School will be in the same city and will feature teams in the same venues or just minutes from the high school facility. Bring your entire program!</li>
              </ul>
            </div>
            <RightImage imageNumber={1} />
          </div>

          {/* Age Groups */}
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <RightImage imageNumber={2} />
            <div className="bg-slate-900 text-white rounded-2xl p-6 flex flex-col justify-center">
              <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-white"><Users className="w-5 h-5 text-white" /> Age Groups</h4>
              <ul className="list-disc pl-6 space-y-2 text-white">
                <li>Each event is open to 11u–14u teams.</li>
              </ul>
            </div>
          </div>

          {/* League Fees */}
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <div className="bg-secondary text-slate-900 rounded-2xl p-6 flex flex-col justify-center">
              <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-slate-900"><DollarSign className="w-5 h-5 text-slate-900" /> League Fees</h4>
              <ul className="list-disc pl-6 space-y-2 text-slate-900">
                <li>Prices are per event. The cost is $400 per tournament. Teams with multiple teams will receive $25 off multi team discount.</li>
                <li>Registration and payment can be made on each event page. Teams will not be placed on the schedule until they are registered and paid for.</li>
                <li>All the events are pay as you go.</li>
              </ul>
            </div>
            <RightImage imageNumber={3} />
          </div>

          {/* Event Format */}
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <RightImage imageNumber={4} />
            <div className="bg-slate-900 text-white rounded-2xl p-6 flex flex-col justify-center">
              <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-white"><Calendar className="w-5 h-5 text-white" /> Event Format</h4>
              <ol className="list-decimal pl-6 space-y-2 text-white">
                <li>Each team will play 4 games at the designated showcase location.</li>
                <li>Each event is a 2-day or 3-day showcase style tournament. Your schedule is mapped out for the entire weekend.</li>
                <li>We accommodate schedule requests in the order registration and fees are paid along with the schedule request payment option.</li>
                <li>The Bracket Finale will be in St. Louis. The Bracket will take place over 3 days depending on location and number of teams.</li>
              </ol>
            </div>
          </div>

          {/* Live Stream */}
          <div className="text-center">
            <h4 className="text-2xl font-black mb-3 flex items-center gap-2 justify-center"><Video className="w-5 h-5 text-secondary" /> Live Stream</h4>
            <p className="text-gray-700 mb-4">ALL RecruitLook Hoops Events will be Live Streamed through BallerTV for College Coaches and Fans to enjoy!</p>
            <Button asChild variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-black">
              <a href="https://www.ballertv.com/organizations/recruitlook" target="_blank" rel="noopener noreferrer">Visit BallerTV.com/RecruitLook</a>
            </Button>
          </div>

          {/* Media Coverage */}
          <div className="mt-16">
            <div className="text-center mb-10">
              <Badge className="bg-secondary text-black px-4 py-2 font-black mb-3">MEDIA COVERAGE</Badge>
              <h3 className="text-3xl lg:text-5xl font-black">Media Coverage Examples</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 text-secondary font-black mb-2"><Star className="w-5 h-5" /> Recruiting Coverage</div>
                <p className="text-gray-700">A quick way to highlight a player that has performed well. We'll tweet before, during, and after events to promote players.</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 text-secondary font-black mb-2"><Video className="w-5 h-5" /> Video Highlights</div>
                <p className="text-gray-700">Our media team is filming games, editing clips, and pushing out highlight videos during the event for more exposure.</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 text-secondary font-black mb-2"><MapPin className="w-5 h-5" /> Event Pics</div>
                <p className="text-gray-700">Our media team took pictures of all the teams that competed in RecruitLook Hoops events that were available for download and print.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PerkCard({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
      <DollarSign className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
      <h4 className="text-xl font-black mb-2">{title}</h4>
      <ul className="space-y-1 text-gray-700">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </div>
  );
}

function RightImage({ imageNumber = 1 }: { imageNumber?: number }) {
  const images = [
    juniorsLeagueFormat,
    juniorsAgeGroups, 
    juniorsLeagueFees,
    sectionImage4
  ];
  
  const alts = [
    "Young basketball player shooting during RecruitLook Hoops Juniors Circuit game",
    "Young basketball player dribbling during RecruitLook Hoops Juniors Circuit game",
    "Coach instructing youth basketball team during RecruitLook Hoops Juniors Circuit",
    "Team celebration photo with RecruitLook Hoops"
  ];

  return (
    <div className="rounded-2xl overflow-hidden">
      <img
        src={images[imageNumber - 1]}
        alt={alts[imageNumber - 1]}
        className="w-full h-full object-cover"
      />
    </div>
  );
}